import { Button, Stack, Typography } from '@mui/material'
import React, { useCallback, useMemo } from 'react'

import { isTeamExternalIntegrationAzure } from 'components/common/mapped-entities/azure/authentication/azure-integration-utils'
import { isTeamExternalIntegrationGcp } from 'components/common/mapped-entities/gcp/authentication/gcp-integration-utils'
import PageSection from 'components/common/PageSection'
import { SearchField } from 'components/common/SearchField'
import { StyledRouterLink } from 'components/common/StyledRouterLink'
import Icon from 'components/Icon'
import { TeamIntegrationTable } from 'components/status-checks/common/team-integration-table/TeamIntegrationTable'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusChecksSearchContext } from 'components/status-checks/StatusChecksSearchState'
import {
  ERROR_MESSAGES_EXTERNAL_INTEGRATIONS,
  useStatusCheckExternalIntegrationsContext
} from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import useSearch from 'hooks/use-search'
import { type TeamExternalIntegration } from 'types/api/status-checks/status-check'
import { naturalSortBy } from 'utils/list'
import { messageFromResponseError } from 'utils/strings'

/**
 * Only non-Azure and non-GCP integrations should be displayed.
 * Integrations must also have a key added, which means the
 * `lastAuthenticationStatus` field should exist.
 */
function isValidSavedIntegration(integration: TeamExternalIntegration) {
  return (
    integration.lastAuthenticationStatus &&
    !isTeamExternalIntegrationGcp(integration) &&
    !isTeamExternalIntegrationAzure(integration)
  )
}

export const AuthenticationList: React.FC = () => {
  const statusChecksUrls = getStatusChecksUrls()

  const {
    hasErrorExternalIntegrations,
    externalIntegrationsError,
    isFetchingExternalIntegrations,
    teamExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()

  const sortedIntegrations = useMemo(() => {
    const savedIntegrations = teamExternalIntegrations.filter(
      isValidSavedIntegration
    )

    return naturalSortBy(savedIntegrations, 'name', 'asc')
  }, [teamExternalIntegrations])

  const searchState = useStatusChecksSearchContext()
  const { searchValue = '', setSearchValue } = searchState.authentications

  // search setup
  const searchMapFn = useCallback(
    (integration: TeamExternalIntegration) =>
      [integration.name].filter(Boolean),
    []
  )
  const searchIntegrations = useSearch(sortedIntegrations, searchMapFn)
  const filteredIntegrations = useMemo(
    () => searchIntegrations(searchValue),
    [searchValue, searchIntegrations]
  )

  const handleSearchInputChange = (value: string) => {
    setSearchValue(value)
  }

  const handleOnChange = async () => {
    setSearchValue('')
  }

  return (
    <Stack
      data-test-status-check-authentication-list
      sx={{ rowGap: 2, marginTop: 3 }}
    >
      <Stack
        direction="row"
        sx={{ columnGap: 5, justifyContent: 'space-between', marginBottom: 2 }}
      >
        <Typography>
          Observability Tools are defined during Health Check creation so your
          team can reuse the tool's definitions and authentications.
        </Typography>
        <Button
          data-test-create-status-check-authentication
          component={StyledRouterLink}
          size="small"
          startIcon={<Icon icon="plus" />}
          sx={{ height: 'fit-content', minWidth: 'fit-content' }}
          to={statusChecksUrls.newAuthentication()}
          variant="contained"
        >
          Authentication
        </Button>
      </Stack>
      <PageSection
        errorMessage={messageFromResponseError(
          externalIntegrationsError,
          ERROR_MESSAGES_EXTERNAL_INTEGRATIONS.unknownFailure
        )}
        hasError={hasErrorExternalIntegrations}
        isLoading={isFetchingExternalIntegrations}
      >
        <Stack>
          <Stack direction="row">
            <SearchField
              data-test-status-check-auth-search
              disabled={
                isFetchingExternalIntegrations || sortedIntegrations.length < 1
              }
              label="Search"
              size="small"
              value={searchValue}
              onChange={handleSearchInputChange}
            />
          </Stack>
          <TeamIntegrationTable
            savedIntegrations={filteredIntegrations}
            onChange={handleOnChange}
          />
        </Stack>
      </PageSection>
    </Stack>
  )
}
